/**
 * The backend wraps every response as `{ status, message, data }`. When something else comes back
 * — an SPA HTML fallback, a proxy error page, a shape change — reading `.data` yields `undefined`,
 * and a component that maps over it takes the whole admin panel down with it. Checking the shape
 * here turns that into an ordinary caught error the page can report.
 */

const payloadOf = (response: unknown): unknown =>
  (response as { data?: unknown } | null | undefined)?.data;

export class UnexpectedPayloadError extends Error {
  constructor(what: string) {
    super(`The API returned an unexpected ${what} payload.`);
    this.name = "UnexpectedPayloadError";
  }
}

export const unwrapList = <T>(response: unknown, what: string): T[] => {
  const payload = payloadOf(response);
  if (!Array.isArray(payload)) {
    throw new UnexpectedPayloadError(what);
  }
  return payload as T[];
};

export const unwrapObject = <T>(response: unknown, what: string): T => {
  const payload = payloadOf(response);
  if (
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    throw new UnexpectedPayloadError(what);
  }
  return payload as T;
};
